/**
 * W46 — Role Home Dashboard.
 * 5-7 KPI tiles per role: open pipe $, weighted pipe $, weakest CP,
 * top risk account, days-since-last-touch leaders, activity delta.
 * D-098: composite_score=0 → "Not yet scored" tile, not a broken zero.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";
import { KpiTile } from "@/components/charts/svg/KpiTile";

interface RoleHomeData {
  tiles: {
    openPipe: number;
    weightedPipe: number;
    weakestCp: { account: string; score: number } | null;
    topRiskAccount: { account: string; daysSince: number } | null;
    daysSinceLeaders: { account: string; daysSince: number }[];
    activityDelta: { thisWeek: number; lastWeek: number };
  };
  accountCount: number;
}

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

const ROLE_LABELS: Record<string, string> = {
  ae: "Account Executive", bdr: "BDR", manager: "Manager",
  executive: "Executive", se: "SE", marketing: "Marketing",
};

export default function RoleHome({ role }: { role: string }) {
  const session = getSession();
  const [data, setData] = useState<RoleHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }
    const userId = session?.id || "me";
    fetch(`/api/dashboards/role-home/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { console.error("[role-home]", e); setError("Could not load dashboard data"); setLoading(false); });
  }, [session?.token]);

  const actDelta = data?.tiles.activityDelta;
  const actTrend = actDelta == null ? null
    : actDelta.thisWeek > actDelta.lastWeek ? "up"
    : actDelta.thisWeek < actDelta.lastWeek ? "down" : "flat";

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-[15px] font-semibold text-foreground">
          {ROLE_LABELS[role] || role.toUpperCase()} Overview
        </h2>
        {data && !loading && (
          <p className="text-[12px] text-muted-foreground mt-0.5">
            {data.accountCount} account{data.accountCount !== 1 ? "s" : ""} in view
          </p>
        )}
        {error && <p className="text-[12px] text-[#DC2626] mt-0.5">{error}</p>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiTile label="Open Pipeline" value={loading ? null : fmt(data?.tiles.openPipe ?? 0)}
          colorVariant="teal" loading={loading} />
        <KpiTile label="Weighted Pipeline" value={loading ? null : fmt(data?.tiles.weightedPipe ?? 0)}
          colorVariant="teal" loading={loading} subValue="Bucket-weighted forecast" />
        <KpiTile label="Weakest Account Score"
          value={loading ? null : data?.tiles.weakestCp == null ? "None scored" : data.tiles.weakestCp.score}
          subValue={data?.tiles.weakestCp?.account}
          colorVariant={!data?.tiles.weakestCp ? "default" : data.tiles.weakestCp.score < 50 ? "red" : data.tiles.weakestCp.score < 70 ? "orange" : "yellow"}
          loading={loading} unscored={!loading && data?.tiles.weakestCp == null} />
        <KpiTile label="Top Risk Account"
          value={loading ? null : data?.tiles.topRiskAccount?.account ?? "None"}
          subValue={data?.tiles.topRiskAccount ? `${data.tiles.topRiskAccount.daysSince}d since last touch` : undefined}
          colorVariant={(data?.tiles.topRiskAccount?.daysSince ?? 0) > 30 ? "red" : (data?.tiles.topRiskAccount?.daysSince ?? 0) > 14 ? "orange" : "default"}
          loading={loading} />
        <KpiTile label="Activity This Week"
          value={loading ? null : actDelta?.thisWeek ?? 0}
          subValue={`Last week: ${actDelta?.lastWeek ?? 0}`}
          trend={actTrend}
          trendLabel={actDelta == null ? undefined : actDelta.thisWeek === actDelta.lastWeek ? "Same as last week"
            : actDelta.thisWeek > actDelta.lastWeek ? `+${actDelta.thisWeek - actDelta.lastWeek} vs last week`
            : `${actDelta.thisWeek - actDelta.lastWeek} vs last week`}
          colorVariant={actTrend === "up" ? "green" : actTrend === "down" ? "orange" : "default"}
          loading={loading} />
        <KpiTile label="Accounts in View"
          value={loading ? null : data?.accountCount ?? 0} loading={loading} />
      </div>

      {!loading && (data?.tiles.daysSinceLeaders.length ?? 0) > 0 && (
        <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-4">
          <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Longest since last touch
          </h3>
          <div className="flex flex-col gap-2">
            {data!.tiles.daysSinceLeaders.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-[13px]">
                <span className="text-foreground truncate max-w-[60%]">{item.account}</span>
                <span className={`tabular-nums font-medium ${
                  item.daysSince > 30 ? "text-[#DC2626]" : item.daysSince > 14 ? "text-[#EA580C]" : "text-muted-foreground"
                }`}>{item.daysSince}d</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
